import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from 'src/app/services/user.service';
import { GlobalService } from 'src/app/services/global.service';
import { ApiService } from 'src/app/services/api.service';
import { DistributionApiService } from '../../distribution/distribution-api.service';
import { GrowthToolsDataService } from '../../growth-tools/services/growth-tools-data.service';

type VideoReleaseState = 'locked' | 'eligible' | 'awaiting_form' | 'in_process' | 'live' | 'completed' | 'terminated';

@Component({
  selector: 'sd-releases-detail',
  templateUrl: './releases-detail.component.html',
  styleUrls: ['./releases-detail.component.scss']
})
export class ReleasesDetailComponent implements OnInit, OnDestroy {
  @Input() song: any;
  @Output() onBack = new EventEmitter<void>();

  isAgreementSigned: boolean = false;
  selectedAgreement: any = null;
  selectedAgreementIndex: number | null = null;
  agreements: any[] = [];
  agreementPreview: string = '';
  agreementPreviews: string[] = [];

  pubAgreement: any = null;
  pubIndex: number = -1;
  syncAgreement: any = null;
  syncIndex: number = -1;
  distAgreement: any = null;
  distIndex: number = -1;

  countries: any[] = [];
  selectedflag: string = "IN";
  flagImageUrl = "https://songdewnetwork.com/sgmedia/country/";

  videoReleasePrice: number = 300;
  dolbyReleasePrice: number = 5000;

  isDolbyEnhanceExpanded: boolean = true;

  isEnhanceExpanded: boolean = true;

  platformIcons: { [key: string]: string } = {
    spotify: '../../../assets/images/availability-spotify-icon.png',
    appleMusic: '../../../assets/images/availability-applemusic-icon.png',
    saavn: '../../../assets/images/availability-jiosaavn-icon.png',
  };

  private static readonly AVAILABILITY_PLATFORM_ORDER = ['spotify', 'appleMusic', 'saavn'];

  otpForm: UntypedFormGroup;
  otpSend: boolean = false;
  otpSendStatus: string = "";
  otpTimerStart: boolean = false;
  timerMinute: number = 1;
  timerSecond: number = 0;
  disabledOTPInput: boolean = false;
  editMobileNumber: boolean = false;
  phonenumber: string = "";
  formSubmit: boolean = false;
  verifySuccess: boolean = false;
  verifyOTPError: boolean = false;
  finalErrorMsg: string = "";
  sendOtpInterval: any;

  albumTrackSlugs: { [mid: number]: string } = {};

  trackDetails: { [mid: number]: { duration?: string; isrc?: string } } = {};

  artistNames: string = '';

  constructor(
    private user: UserService,
    public global: GlobalService,
    private globalApi: ApiService,
    private api: DistributionApiService,
    private router: Router,
    private growthToolsData: GrowthToolsDataService
  ) {}

  ngOnInit() {
    this.initializeOtpForm();
    this.getOppCountries();

    this.growthToolsData.getDolbyPrice().subscribe({
      next: price => this.dolbyReleasePrice = price,
      error: () => { }
    });

    this.growthToolsData.getSpotifyVideoPrice().subscribe({
      next: price => this.videoReleasePrice = price,
      error: () => { }
    });

    console.log('[VIDEO RELEASE DEBUG] song.pdlIssue:', this.song?.pdlIssue);

    if (this.song) {
      if (this.song.processedAgreements) {
        this.agreements = this.song.processedAgreements;
        this.agreementPreviews = this.song.agreementPreviews || [];
      }
      this.agreementPreview = this.song.agreementPreview || '';
      
      this.agreements.forEach((a, idx) => {
        if (a.type === 'Publishing') {
          this.pubAgreement = a;
          this.pubIndex = idx;
        } else if (a.type === 'Sync') {
          this.syncAgreement = a;
          this.syncIndex = idx;
        } else if (a.type === 'Distribution') {
          this.distAgreement = a;
          this.distIndex = idx;
        }
      });

      this.getAgreementSrcStatus(this.agreements);

      if (this.song.album_id > 0) {
        this.loadAlbumTrackDetails();
      } else {
        this.loadSongTrackDetails();
      }
    }
  }

  private loadAlbumTrackDetails() {
    this.globalApi.getAlbumDetails(this.song.slug).subscribe((res: any) => {
      if (res?.success) {
        (res.data.music_tracks || []).forEach((track: any) => {
          this.albumTrackSlugs[track.id] = track.slug_usersong;
          this.trackDetails[track.id] = { duration: track.duration, isrc: track.isrc };
        });
        this.artistNames = (res.data.artists || []).map((a: any) => a.full_name).filter(Boolean).join(', ');
      }
    });
  }

  private loadSongTrackDetails() {
    this.globalApi.getSongDetails(this.song.slug).subscribe((res: any) => {
      if (res?.success) {
        this.trackDetails[this.song.mid] = { duration: res.result?.duration, isrc: res.result?.isrc };
        this.artistNames = (res.result?.artists || []).map((a: any) => a.full_name).filter(Boolean).join(', ');
      }
    });
  }

  private dolbyUrlSegments(track: any): any[] | null {
    const isAlbum = this.song?.album_id > 0;
    const slug = isAlbum ? this.albumTrackSlugs[track.mid] : this.song.slug;
    if (!slug) {
      console.error('No track slug resolved for Dolby Atmos release', track);
      return null;
    }
    return ['/growth-tools/dolby-atmos', 'song', slug];
  }

  goToDolbyUpload(track: any) {
    const segments = this.dolbyUrlSegments(track);
    if (segments) {
      this.router.navigate(segments);
    }
  }

  payForDolbyRelease(track: any) {
    if (this.hasDolbyRelease(track)) {
      const segments = this.dolbyUrlSegments(track);
      if (segments) {
        this.router.navigate([...segments, 'upload']);
      }
    } else {
      this.goToDolbyUpload(track);
    }
  }

  getDolbyReleaseStatus(track: any): { state: VideoReleaseState; label: string; subLabel?: string } {
    return this.computeDolbyReleaseStatus(track);
  }

  private getTrackDolbyEntry(track: any): any {
    const raw = this.song?.dolby_atmos || this.song?.dolby;
    const entries: any[] = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
    if (!entries.length) return null;
    return entries.find((v: any) => (v.songid === track?.mid || v.song_id === track?.mid)) || null;
  }

  private hasDolbyRelease(track: any): boolean {
    const entry = this.getTrackDolbyEntry(track);
    return !!(entry?.dolby_id || entry?.id || entry?.dolby_atmos_id);
  }

  private computeDolbyReleaseStatus(track: any): { state: VideoReleaseState; label: string; subLabel?: string } {
    if (this.getDistributionStatus() !== 'Live') {
      return { state: 'locked', label: 'Available after release approval', subLabel: 'Your release is currently under review' };
    }

    const entry = this.getTrackDolbyEntry(track);
    const entryId = entry?.dolby_id ?? entry?.id ?? entry?.dolby_atmos_id;
    if (!entryId) {
      return { state: 'eligible', label: 'Eligible for upload' };
    }

    if (entry.status === null || entry.status === undefined || entry.status === '') {
      return { state: 'awaiting_form', label: 'Payment complete' };
    }

    return ReleasesDetailComponent.VIDEO_STATUS_BY_CODE[Number(entry.status)]
      || { state: 'in_process', label: 'In Process' };
  }

  private videoUrlSegments(track: any): any[] | null {
    const isAlbum = this.song?.album_id > 0;
    const slug = isAlbum ? this.albumTrackSlugs[track.mid] : this.song.slug;
    if (!slug) {
      console.error('No track slug resolved for video release', track);
      return null;
    }
    return ['/growth-tools/spotify-video', 'song', slug];
  }

  goToVideoUpload(track: any) {
    const segments = this.videoUrlSegments(track);
    if (segments) {
      this.router.navigate(segments);
    }
  }

  payForVideoRelease(track: any) {
    if (this.hasSpotifyVideo(track)) {
      const segments = this.videoUrlSegments(track);
      if (segments) {
        this.router.navigate([...segments, 'upload']);
      }
    } else {
      this.goToVideoUpload(track);
    }
  }

  getTrackDuration(track: any): string {
    const duration = this.trackDetails[track?.mid]?.duration;
    return duration ? this.global.sec2TimeDuration(duration) : '';
  }

  getTrackIsrc(track: any): string {
    return this.trackDetails[track?.mid]?.isrc || track?.isrc || '';
  }

  getAvailabilityPlatforms(): any[] {
    const links = this.song?.platformLinks || [];
    return ReleasesDetailComponent.AVAILABILITY_PLATFORM_ORDER
      .map(name => links.find((pf: any) => pf.name === name))
      .filter(Boolean);
  }

  getVideoReleaseStatus(track: any): { state: VideoReleaseState; label: string; subLabel?: string } {
    return this.computeVideoReleaseStatus(track);
  }

  private getTrackVideoEntry(track: any): any {
    const raw = this.song?.spotify_video;
    const videos: any[] = Array.isArray(raw) ? raw : (raw && typeof raw === 'object' ? [raw] : []);
    if (!videos.length) return null;
    return videos.find((v: any) => v.songid === track?.mid) || null;
  }

  private hasSpotifyVideo(track: any): boolean {
    return !!this.getTrackVideoEntry(track)?.spotify_video_id;
  }

  private static readonly VIDEO_STATUS_BY_CODE: { [code: number]: { state: VideoReleaseState; label: string } } = {
    0: { state: 'in_process', label: 'In Process' },
    1: { state: 'live', label: 'Live' },
    2: { state: 'completed', label: 'Completed' },
    3: { state: 'terminated', label: 'Terminated' }
  };

  private computeVideoReleaseStatus(track: any): { state: VideoReleaseState; label: string; subLabel?: string } {
    if (this.getDistributionStatus() !== 'Live') {
      return { state: 'locked', label: 'Available after release approval', subLabel: 'Your release is currently under review' };
    }

    const video = this.getTrackVideoEntry(track);
    if (!video?.spotify_video_id) {
      return { state: 'eligible', label: 'Eligible for upload' };
    }

    if (video.status === null || video.status === undefined || video.status === '') {
      return { state: 'awaiting_form', label: 'Payment complete' };
    }

    return ReleasesDetailComponent.VIDEO_STATUS_BY_CODE[Number(video.status)]
      || { state: 'in_process', label: 'In Process' };
  }

  ngOnDestroy() {
    if (this.sendOtpInterval) {
      clearInterval(this.sendOtpInterval);
    }
  }

  get otp() { return this.otpForm?.get('otp') as UntypedFormGroup; }
  get otpPhone() { return this.otpForm?.get('phone') as UntypedFormControl; }
  get countryCode() { return this.otpForm?.get('countryCode') as UntypedFormControl; }

  initializeOtpForm() {
    this.otpForm = new UntypedFormGroup({
      otp: new UntypedFormGroup({
        input1: new UntypedFormControl('', Validators.required),
        input2: new UntypedFormControl('', Validators.required),
        input3: new UntypedFormControl('', Validators.required),
        input4: new UntypedFormControl('', Validators.required),
        input5: new UntypedFormControl('', Validators.required),
        input6: new UntypedFormControl('', Validators.required)
      }),
      phone: new UntypedFormControl(this.user.mobile, [Validators.required, Validators.pattern(/^[0-9]*$/)]),
      countryCode: new UntypedFormControl('+91', [Validators.required]),
      whatsappMode: new UntypedFormControl(true)
    });

    const num = this.user?.mobile?.toString() || '';
    let len = num.length - 4;
    if (len < 0) len = 0;

    const mask = '*'.repeat(len);
    this.phonenumber = this.otpForm.value.countryCode + num.substring(0, 2) + mask + num.substring(8);
    this.otpForm.patchValue({ phone: this.user.mobile, countryCode: this.otpForm.value.countryCode });
  }

  getOppCountries() {
    this.globalApi.getOppCountries().subscribe((res: any) => {
      this.countries = res;
    });
  }

  changeSelectedCountryCode(value: string, code: any): void {
    this.selectedflag = value;
    this.otpForm.patchValue({ countryCode: '+' + code });
  }

  getAgreementSrcStatus(agreements: any[]) {
    agreements.forEach(agreement => {
      switch(agreement.type){
        case 'Publishing':
          agreement.src = agreement.status == 'signed' ? '../../../assets/images/upload/Publishing.svg' : '../../../assets/images/upload/Publishing_grey.svg';
          break;
        case 'Sync':
          agreement.src = agreement.status == 'signed' ? '../../../assets/images/upload/Sync.svg' : '../../../assets/images/upload/Sync_grey.svg';
          break;
        default:
          agreement.src = agreement.status == 'signed' ? '../../../assets/images/upload/Publishing.svg' : '../../../assets/images/upload/Publishing_grey.svg';
      }
    });
  }

  openPdf(song: any) {
    const pdfUrl = song?.signed_agreement || '/assets/sample-distribution-agreement.pdf';
    window.open(pdfUrl, '_blank');
  }

  openAgreement(agreement: any, index: number) {
    this.selectedAgreement = agreement;
    this.selectedAgreementIndex = index;
    this.isAgreementSigned = this.selectedAgreement?.status === 'signed';

    setTimeout(() => {
      const modal = document.getElementById('exampleModalCenter');
      if (modal && (window as any).$) {
        (window as any).$('#exampleModalCenter').modal('show');
      } else if (modal && (window as any).bootstrap && (window as any).bootstrap.Modal) {
        const bootstrapModal = (window as any).bootstrap.Modal.getOrCreateInstance(modal);
        if (bootstrapModal) {
          bootstrapModal.show();
        }
      }
    }, 0);
  }

  isOtpComplete(): boolean {
    if (!this.otp) return false;
    for (let i = 1; i <= 6; i++) {
      const v = this.otp.get('input' + i)?.value;
      if (v === null || v === undefined || v.toString().trim() === '') {
        return false;
      }
    }
    return true;
  }

  editOtpNumber() {
    this.editMobileNumber = true;
    this.otpSend = false;
    this.otpSendStatus = '';
    this.verifySuccess = false;
    this.verifyOTPError = false;
    this.otp?.reset();
    this.formSubmit = false;
  }

  sendOtp() {
    if (!this.selectedAgreement) {
      console.error('Agreement is undefined');
      return;
    }

    this.otpSendStatus = '';

    if (this.editMobileNumber) {
      const num = this.otpPhone.value.toString();
      let len = num.length - 4;
      len = len < 0 ? 0 : len;
      const mask = '*'.repeat(len);
      this.phonenumber = this.countryCode.value + num.substring(0, 2) + mask + num.substring(8);
      this.editMobileNumber = false;
    }

    this.otp?.reset();
    this.formSubmit = false;
    this.verifyOTPError = false;
    this.verifySuccess = false;
    this.finalErrorMsg = '';

    this.timerMinute = 1;
    this.timerSecond = 0;
    this.otpTimerStart = true;

    this.sendOtpInterval = setInterval(() => {
      if (this.timerMinute >= 0) {
        if (this.timerSecond > 0) {
          this.timerSecond -= 1;
        } else {
          if ((this.timerMinute - 1) < 0) {
            this.clearOtpInterval();
          } else {
            this.timerMinute -= 1;
            this.timerSecond = 59;
          }
        }
      } else {
        this.clearOtpInterval();
      }
    }, 1000);

    const mobileNumber = this.countryCode.value + this.otpPhone.value;
    const payload = {
      agreement_id: this.selectedAgreement?.agrid,
      media_id: this.song?.mid,
      media_type: this.song?.album_id > 0 ? 'album' : 'song',
      mobile_number: mobileNumber
    };

    this.api.termSendOtp(payload).subscribe(
      res => {
        if (res.result === "Success") {
          this.otpSend = true;
          this.handleOtpInput();
          this.otpSendStatus = "Enter the 6-digit code sent to your phone";
          this.global.ga4Event('Distribution', 'otp_request', this.song.title, 'single');
        }
      },
      (err: HttpErrorResponse) => {
        this.otpSendStatus = "OTP send failed, Try again later!";
        this.otpSend = false;
        this.clearOtpInterval();
      }
    );
  }

  verifyOtp() {
    this.formSubmit = true;
    this.verifyOTPError = false;
    this.verifySuccess = false;
    this.finalErrorMsg = '';

    const values = Object.values(this.otp?.value || {});
    const otpStr = values.join('').substring(0, 6);

    const mobileNumber = this.otpForm.value.countryCode + this.otpForm.value.phone;
    const payload = {
      otp: otpStr,
      agreement_id: this.selectedAgreement?.agrid,
      media_id: this.song?.mid,
      media_type: this.song?.album_id > 0 ? 'album' : 'song',
      mobile_number: mobileNumber
    };

    this.disabledOTPInput = true;

    this.api.termVerifyOtp(payload).subscribe(
      res => {
        this.disabledOTPInput = false;
        if (res.result === "Success") {
          this.verifySuccess = true;
          this.finalErrorMsg = 'OTP verified successfully';
          this.verifyOTPError = false;

          setTimeout(() => {
            this.closeOtpModal();
            window.location.reload();
          }, 800);
        } else {
          this.verifyOTPError = true;
          this.finalErrorMsg = res.msg || 'OTP verification failed';
        }
      },
      err => {
        this.disabledOTPInput = false;
        this.verifyOTPError = true;
        this.finalErrorMsg = 'Error verifying OTP';
      }
    );
  }

  splitNumber(el: any, data: string) {
    const formControlName = el.getAttribute('formcontrolname');
    this.otpForm.get('otp').get(formControlName).setValue(data[0]);
    data = data.substring(1);
    if (el.nextElementSibling && data.length) {
      el.nextElementSibling.focus();
      this.splitNumber(el.nextElementSibling, data);
    }
  }

  handleOtpInput() {
    setTimeout(() => {
      const inputElements = document.querySelectorAll('.form-control-otp');
      const firstOtpInput = document.querySelector('.form-control-otp') as HTMLInputElement;

      inputElements.forEach(elem => {
        const angularthis = this;
        elem.addEventListener('keyup', function (e: KeyboardEvent) {
          const ignoredKeys = ['Shift', 'Tab', 'Meta', 'Alt', 'Control'];
          if (ignoredKeys.includes(e.key)) { return; }

          if ((e.key === 'ArrowLeft' || e.key === 'Backspace') && (this as any).previousElementSibling) {
            (this as any).previousElementSibling.focus();
          } else if (e.key !== 'Backspace' && (this as any).nextElementSibling) {
            (this as any).nextElementSibling.focus();
          }

          if ((e.target as HTMLInputElement).value.length > 1) {
            angularthis.splitNumber(e.target, (e.target as HTMLInputElement).value);
          }
        });

        elem.addEventListener('focus', function (e) {
          if (this === firstOtpInput) return;
          if (firstOtpInput.value === '') {
            firstOtpInput.focus();
          }
          if ((this as any).previousElementSibling && (this as any).previousElementSibling.value === '') {
            (this as any).previousElementSibling.focus();
          }
        });
      });
    }, 500);
  }

  clearOtpInterval() {
    this.otpTimerStart = false;
    if (this.sendOtpInterval) {
      clearInterval(this.sendOtpInterval);
    }
  }

  closeOtpModal() {
    try {
      const modal = document.getElementById('exampleModalCenter');
      if (modal && (window as any).bootstrap && (window as any).bootstrap.Modal) {
        const bootstrapModal = (window as any).bootstrap.Modal.getInstance(modal);
        if (bootstrapModal) {
          bootstrapModal.hide();
        }
      }
    } catch (e) {
    }
    if ((window as any).$) {
      (window as any).$('#exampleModalCenter').modal('hide');
    }
    this.resetOtpStates();
  }

  resetOtpStates() {
    this.otpForm.reset({
      phone: this.user.mobile,
      countryCode: '+91',
      whatsappMode: true
    });

    this.otpSend = false;
    this.otpSendStatus = '';
    this.otpTimerStart = false;
    this.timerMinute = 1;
    this.timerSecond = 0;
    this.disabledOTPInput = false;
    this.verifyOTPError = false;
    this.verifySuccess = false;
    this.finalErrorMsg = '';
    this.formSubmit = false;
    this.editMobileNumber = false;

    const numStr = (this.user.mobile || '').toString();
    this.phonenumber = this.otpForm.value.countryCode + numStr.substring(0, 2) + '*'.repeat(numStr.length - 4) + numStr.substring(8);

    if (this.sendOtpInterval) {
      this.clearOtpInterval();
    }
  }

  goBack() {
    this.onBack.emit();
  }

  completeRelease() {
    this.global.gAEvents('my_release', 'complete_release', this.song.title, this.song.album_id == 0 ? 'single' : 'album');
    if (this.song.album_id == 0) {
      this.router.navigate(['distribute/music/song'], { queryParams: { slug: this.song.slug } });
    } else {
      this.router.navigate(['distribute/music/album'], { queryParams: { slug: this.song.slug } });
    }
  }

  hasIssue(): boolean {
    return !!(this.song.trackIssue?.artworkIssue || this.song.trackIssue?.pdlIssue || this.song.trackIssue?.mIssue);
  }

  getIssueHeadline(): string {
    const causes: string[] = [];
    if (this.song.trackIssue?.artworkIssue) causes.push('Artwork');
    if (this.song.trackIssue?.pdlIssue) causes.push('Audio');
    if (this.song.trackIssue?.mIssue) causes.push('Metadata/Legal issue');
    return causes.length ? `Issue Detected : ${causes.join(' & ')}` : '';
  }

  getIssueMessages(): string[] {
    const messages: string[] = [];
    if (this.song.trackIssue?.mIssue) {
      messages.push('Please check your email for the metadata/legal issue.');
    }
    if (this.song.trackIssue?.artworkIssue) {
      const msg = this.song.artIssue?.[0]?.msg;
      if (msg) messages.push(msg);
    }
    return messages;
  }

  canUploadFix(): boolean {
    return !!this.song.trackIssue?.artworkIssue;
  }

  getDistributionStatus(): string {
    if (this.hasIssue()) {
      return 'Action Required';
    }

    const status = this.song?.distribution_status;
    if (!status || status.length === 0) return 'Drafts';

    if (status[0]?.status === 'Terminated') return 'Terminated';
    if (status[0]?.status === 'Hold By Songdew' && !status[0]?.on) return 'On hold';
    if (status[3]?.status === 'Live' && status[3]?.on) return 'Live';
    if (status[2]?.status === 'Scheduled' && status[2]?.on && !status[3]?.on) return 'Scheduled';
    if (status[1]?.status === 'In Process' && status[1]?.on && !status[2]?.on) return 'In Process';
    if (status[1]?.status === 'Clarifications Required' && status[1]?.on && !status[2]?.on) return 'In Process';
    if (status[1]?.status === 'Processed' && status[1]?.on && !status[2]?.on) return 'In Process';
    if (status[0]?.status === 'Submitted' && status[0]?.on && !status[1]?.on) return 'Submitted';
    if (status[0]?.status === 'Drafts' && status[0]?.on && !status[1]?.on) return 'Drafts';

    return 'Unknown';
  }

  isVideoReleaseEligible(): boolean {
    return this.getDistributionStatus() === 'Live';
  }

  getDistributionStatusClass(): string {
    const statusStr = this.getDistributionStatus();
    switch (statusStr) {
      case 'Live':
        return 'badge-live';
      case 'Action Required':
        return 'badge-action-required';
      case 'Terminated':
      case 'On hold':
        return 'badge-terminated';
      case 'Drafts':
        return 'badge-draft';
      case 'Submitted':
      case 'In Process':
      case 'Scheduled':
      default:
        return 'badge-in-process';
    }
  }

  getLastActiveStatus(): string {
    const dist = this.song?.distribution_status || [];
    const item = dist.find((status: any, index: number, arr: any[]) =>
      status.on && (index === arr.length - 1 || !arr[index + 1].on)
    );
    return item?.status || 'Submitted';
  }

  getStepperActiveIndex(): number {
    const dist = this.song?.distribution_status || [];
    let activeIndex = 0;
    for (let i = 0; i < dist.length; i++) {
      if (dist[i].on) {
        activeIndex = i;
      }
    }
    return activeIndex;
  }
}
